import { Spinner } from '../../components/Spinner.js';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import type { Spot, SpotCategory } from '@ridehub/shared';
import { CATEGORIES, TAGS } from '@ridehub/shared';
import { apiClient } from '../../lib/api-client.js';
import { useAuth } from '../auth/useAuth.js';
import { Button } from '../../components/Button.js';
import { Input } from '../../components/Input.js';
import { useSpotEdit } from './useSpotEdit.js';

export function SpotEditForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { dbUser } = useAuth();
  const { update, loading, error } = useSpotEdit();

  const [spot, setSpot] = useState<Spot | null>(null);
  const [fetchLoading, setFetchLoading] = useState(true);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [category, setCategory] = useState<SpotCategory>('other');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  useEffect(() => {
    if (!id) return;
    apiClient.get<Spot>(`/spots/${id}`).then((data) => {
      if (!data) return;
      setSpot(data);
      setName(data.name);
      setDescription(data.description ?? '');
      setAddress(data.address ?? '');
      setCategory(data.category as SpotCategory);
      setSelectedTags(data.tags ?? []);
      setFetchLoading(false);
    });
  }, [id]);

  // 本人以外はスポット詳細にリダイレクト
  useEffect(() => {
    if (!fetchLoading && spot && dbUser && spot.userId !== dbUser.id) {
      navigate(`/spots/${id}`, { replace: true });
    }
  }, [fetchLoading, spot, dbUser, id, navigate]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    const updated = await update(id, {
      name,
      description: description || undefined,
      address: address || undefined,
      category,
      tags: selectedTags,
    });

    if (updated) {
      navigate(`/spots/${id}`, { replace: true });
    }
  };

  if (fetchLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (!spot) {
    return <p className="text-center py-8 text-gray-500">スポットが見つかりません</p>;
  }

  return (
    <div className="p-4 max-w-lg mx-auto">
      <h1 className="text-xl font-bold mb-6">スポットを編集</h1>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          label="スポット名"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={100}
        />

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">カテゴリ *</label>
          <div className="grid grid-cols-3 gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                type="button"
                onClick={() => setCategory(cat.value)}
                className={[
                  'flex flex-col items-center gap-1 p-2 rounded-lg border text-xs transition-colors',
                  category === cat.value
                    ? 'border-primary bg-primary-50 text-primary'
                    : 'border-gray-200 hover:border-gray-300',
                ].join(' ')}
              >
                <span className="text-xl">{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">説明</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="スポットについての説明を入力"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <Input
          label="住所"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          maxLength={200}
          placeholder="住所（任意）"
        />

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">タグ</label>
          <div className="grid grid-cols-2 gap-2">
            {TAGS.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={[
                  'flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors text-left',
                  selectedTags.includes(tag)
                    ? 'border-primary bg-primary-50 text-primary'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700',
                ].join(' ')}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="flex-1 border border-gray-300"
            onClick={() => navigate(`/spots/${id}`)}
          >
            キャンセル
          </Button>
          <Button type="submit" loading={loading} size="lg" className="flex-1">
            保存する
          </Button>
        </div>
      </form>
    </div>
  );
}
